import clsx from 'clsx'
import { format } from 'date-fns'
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { ArrowRight, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import Button from '@/components-latest/primitives/Button'
import { Dialog, DialogContent } from '@/components-latest/primitives/Dialog'
import { Cross, ArrowLeft, ExclamationPoint, ClockTimer } from '@/components-latest/primitives/Icons'
import Text from '@/components-latest/primitives/Text'
import { useToaster } from '@/components-latest/ui/Toaster/useToaster/useToaster'
import { useGetStoreBusyStatusQuery, useUpdateStoreBusyStatusMutation, useChangeStoreAvailabilityMutation } from '@/ducks/store/api/store.api'
import { STORE_AVAILABILITY, BUSY_STATUS } from '@/constants/common'
import { logFirebaseEvent } from '@/utils/firebase.utils'

import Styles from './UpdateStoreStatus.module.scss'

// Constants
const BUSY_DURATIONS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 45, label: '45 minutes' },
] as const

// Types
type StatusType = 0 | 1 | 2
type DurationValue = typeof BUSY_DURATIONS[number]['value']

interface UpdateStoreStatusProps {
  isOpen: boolean
  onClose: () => void
  currentStatus: StatusType
  onStatusUpdate: (newStatus: StatusType) => void
  isLoading?: boolean
  showBusyConfirmation?: boolean
  storeId?: string
}

interface StatusOption {
  id: StatusType
  title: string
  description: string
  icon: 'dot' | 'pause'
  iconColor: 'green' | 'grey'
}

const getDurationLabel = (duration: number): string => {
  const durationOption = BUSY_DURATIONS.find(d => d.value === duration)
  return durationOption ? durationOption.label : `${duration} minutes`
}

const UpdateStoreStatus: React.FC<UpdateStoreStatusProps> = ({
  isOpen,
  onClose,
  currentStatus,
  onStatusUpdate,
  isLoading = false,
  showBusyConfirmation = false,
  storeId
}) => {
  const { t } = useTranslation()
  const { addToast } = useToaster()
  // State management
  const [selectedStatus, setSelectedStatus] = useState<StatusType>(currentStatus)
  const [isBusyEnabled, setIsBusyEnabled] = useState(false)
  const [isBusyDurationModalOpen, setIsBusyDurationModalOpen] = useState(false)
  const [selectedDuration, setSelectedDuration] = useState<DurationValue | null>(null)
  const [isBusySummaryOpen, setIsBusySummaryOpen] = useState(false)
  const [isBusyConfirmationOpen, setIsBusyConfirmationOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const { data: busyStatusData, refetch: refetchBusyStatus } = useGetStoreBusyStatusQuery(undefined, {
    skip: !isOpen
  })
  
  const [updateBusyStatus, { isLoading: isUpdatingBusyStatus }] = useUpdateStoreBusyStatusMutation()
  const [changeStoreAvailability] = useChangeStoreAvailabilityMutation()

  // Reset state
  useEffect(() => {
    if (isOpen) {
      setSelectedStatus(currentStatus)
      setIsBusyEnabled(false)
      setIsBusyDurationModalOpen(false)
      setSelectedDuration(null)
      setIsBusySummaryOpen(false)
      setIsBusyConfirmationOpen(false)
      setError(null)
      
      if (showBusyConfirmation && currentStatus === BUSY_STATUS.BUSY) {
        setIsBusyConfirmationOpen(true)
      }
    }
  }, [isOpen, currentStatus, showBusyConfirmation])



  const isOffline = currentStatus === STORE_AVAILABILITY.OFFLINE
  const isBusyDisabled = isOffline

  // Memoized status options
  const statusOptions = useMemo((): StatusOption[] => [
    {
      id: BUSY_STATUS.NON_BUSY,
      title: t('store-status.statuses.accepting-orders'),
      description: '',
      icon: 'dot',
      iconColor: 'green'
    },
    {
      id: BUSY_STATUS.BUSY,
      title: t('store-status.statuses.busy'),
      description: isOffline 
        ? t('store-status.descriptions.busy-offline')
        : t('store-status.descriptions.busy-online'),
      icon: 'pause',
      iconColor: 'grey'
    },
    {
      id: STORE_AVAILABILITY.OFFLINE,
      title: t('store-status.statuses.offline'),
      description: '',
      icon: 'dot',
      iconColor: 'grey'
    }
  ], [isOffline, t])

  const modalTitle = useMemo(() => {
    if (isBusySummaryOpen) return t('store-status.summary-title')
    if (isBusyDurationModalOpen) return t('store-status.duration-title')
    return t('store-status.title')
  }, [isBusySummaryOpen, isBusyDurationModalOpen, t])

  const handleStatusSelect = useCallback((status: StatusType) => {
    setSelectedStatus(status)
    setError(null)
  }, [])

  const handleUpdateStatus = useCallback(async () => {
    if (selectedStatus === currentStatus) return

    try {
      setError(null)
      
      if (selectedStatus === BUSY_STATUS.NON_BUSY) {
        await updateBusyStatus({
          status: BUSY_STATUS.NON_BUSY
        }).unwrap()
        await refetchBusyStatus()
      }
      
      onStatusUpdate(selectedStatus)
      onClose()
    } catch (error) {
      console.error('Failed to update status:', error)
      setError(t('store-status.errors.update-failed'))
    }
  }, [selectedStatus, currentStatus, updateBusyStatus, refetchBusyStatus, onStatusUpdate, onClose])

  const handleBusyToggle = useCallback(() => {
    setIsBusyEnabled(!isBusyEnabled)
    if (!isBusyEnabled) {
      setSelectedStatus(BUSY_STATUS.BUSY)
    }
    setError(null)
    
    // Track busy toggle interaction
    if (storeId) {
      logFirebaseEvent('AC_busy_toggle_interact', {
        Event_Time: format(new Date(), 'dd/MM/yyyy - HH:mm:ss'),
        Store_ID: storeId
      })
    }
  }, [isBusyEnabled, storeId])

  const handleDefineBusyDuration = useCallback(() => {
    setIsBusyDurationModalOpen(true)
    setError(null)
  }, [])

  const handleDurationSelect = useCallback((duration: DurationValue) => {
    setSelectedDuration(duration)
    setError(null)
    
    // Track duration slider interaction
    if (storeId) {
      logFirebaseEvent('AC_busy_slider_interact', {
        Event_Time: format(new Date(), 'dd/MM/yyyy - HH:mm:ss'),
        Store_ID: storeId,
        Slider_Value: duration
      })
    }
  }, [storeId])

  const handleConfirmBusyDuration = useCallback(() => {
    if (selectedDuration === null) return
    setIsBusySummaryOpen(true)
  }, [selectedDuration])

  const handleConfirmBusySummary = useCallback(async () => {
    if (selectedDuration === null) return
    
    try {
      setError(null)
      
      await updateBusyStatus({
        status: BUSY_STATUS.BUSY,
        duration: String(selectedDuration)
      }).unwrap()
      
      // Track busy confirm event
      if (storeId) {
        logFirebaseEvent('AC_busy_confirm', {
          Event_Time: format(new Date(), 'dd/MM/yyyy - HH:mm:ss'),
          Store_ID: storeId
        })
      }
      
      await refetchBusyStatus()
      onStatusUpdate(BUSY_STATUS.BUSY)
      onClose()
    } catch (error) {
      console.error('Failed to update busy status:', error)
      setError(t('store-status.errors.busy-update-failed'))
    }
  }, [selectedDuration, updateBusyStatus, refetchBusyStatus, onStatusUpdate, onClose, storeId, t])

  const handleEditBusyDuration = useCallback(() => {
    setIsBusySummaryOpen(false)
    setIsBusyDurationModalOpen(true)
    setError(null)
  }, [])

  const handleBack = useCallback(() => {
    setIsBusyDurationModalOpen(false)
    setError(null)
  }, [])

  const handleDeactivateBusyMode = useCallback(async () => {
    try {
      setError(null)
      onClose()
      
      // Update busy status to NON_BUSY
      await updateBusyStatus({
        status: BUSY_STATUS.NON_BUSY
      }).unwrap()
      
      // Track busy deactivate event
      if (storeId) {
        logFirebaseEvent('AC_busy_deactivate', {
          Event_Time: format(new Date(), 'dd/MM/yyyy - HH:mm:ss'),
          Store_ID: storeId
        })
      }
      
      if (storeId) {
        await changeStoreAvailability({
          availability: 1,
          store_id: storeId
        }).unwrap()
      }
      

      onStatusUpdate(1)
      
     
      addToast({
        type: 'newOrder',
        message: t('store-notifications.back-online'),
        intent: 'success',
        duration: 5000
      })
      
    
      refetchBusyStatus()
    } catch (error) {
      console.error('Failed to deactivate busy mode:', error)
     
    }
  }, [updateBusyStatus, changeStoreAvailability, storeId, refetchBusyStatus, onStatusUpdate, onClose, addToast, t])

  const handleContinueBusyMode = useCallback(() => {
    onClose()
  }, [onClose])

  // Render functions
  const renderStatusIcon = (option: StatusOption) => {
    if (option.icon === 'dot') {
      return (
        <div
          className={clsx(Styles.dot, {
            [Styles.dotGreen]: option.iconColor === 'green',
            [Styles.dotGrey]: option.iconColor === 'grey'
          })}
        />
      )
    }
    
    if (option.icon === 'pause') {
      return (
        <div className={Styles.pauseIcon}>
          <div className={Styles.pauseBar} />
          <div className={Styles.pauseBar} />
        </div>
      )
    }
    
    return null
  }

  const renderStatusOption = (option: StatusOption) => (
    <div
      key={option.id}
      className={clsx(Styles.option, {
        [Styles.optionSelected]: selectedStatus === option.id,
        [Styles.optionActive]: currentStatus === option.id,
        [Styles.optionDisabled]: option.id === BUSY_STATUS.BUSY && isOffline
      })}
      onClick={() => {
        if (!(option.id === BUSY_STATUS.BUSY && isOffline)) {
          handleStatusSelect(option.id)
        }
      }}>
      <div className={Styles.optionLeft}>
        <div className={Styles.iconContainer}>
          {renderStatusIcon(option)}
        </div>
        <div className={Styles.optionText}>
          <Text variant="nextBody" weight="semiBold" className={Styles.optionTitle}>
            {option.title}
          </Text>
          {option.description && (
            <Text variant="nextCaption" className={Styles.optionDescription}>
              {option.description}
            </Text>
          )}
        </div>
      </div>
      <div className={Styles.optionRight}>
        {option.id === BUSY_STATUS.BUSY ? (
          <div className={Styles.toggleContainer}>
            <button
              className={clsx(Styles.toggle, {
                [Styles.toggleActive]: isBusyEnabled,
                [Styles.toggleDisabled]: isOffline
              })}
              disabled={isOffline}
              onClick={e => {
                e.stopPropagation()
                if (!isOffline) {
                  handleBusyToggle()
                }
              }}>
              <div className={Styles.toggleThumb}>
                {isBusyEnabled && <div className={Styles.toggleCheck}>✓</div>}
              </div>
            </button>
          </div>
        ) : currentStatus === option.id ? (
          <div className={Styles.activeIndicator} />
        ) : (
          <div className={Styles.radioButton} />
        )}
      </div>
    </div>
  )

  const renderSummaryContent = () => (
    <>
      <div className={Styles.durationHeader}>
        <button onClick={handleEditBusyDuration} className={Styles.headerBackButton}>
          <ArrowLeft />
        </button>
       
        <button onClick={onClose} className={Styles.closeButton}>
          <Cross />
        </button>
      </div>
      <div className={Styles.summaryContent}>
        <div className={Styles.summaryIcon}>
          <ClockTimer />
        </div>
        <div className={Styles.summaryText}>
        <Text variant="nextTitle-3" weight="bold" className={Styles.summaryTitle}>
          {t('store-status.summary-title')}
        </Text>
          <Text variant="nextBody" className={Styles.summaryDescription}>
            {t('store-status.summary.duration-message', { duration: selectedDuration })}
            {t('store-status.summary.no-orders-message')}
          </Text>
        </div>
      </div>
    </>
  )

  const renderDurationOptions = () => (
    <>
      <div className={Styles.durationHeader}>
        <button onClick={handleBack} className={Styles.headerBackButton}>
          <ArrowLeft />
        </button>
        <Text variant="nextTitle-3" weight="bold" className={Styles.durationTitle}>
          {t('store-status.set-duration-title')}
        </Text>
        <button onClick={onClose} className={Styles.closeButton}>
          <Cross />
        </button>
      </div>
      <div className={Styles.inReview}>
        <ExclamationPoint />
        <Text variant="body" weight="semiBold">
          {t('store-status.duration.info-message')}
        </Text>
      </div>
      <div className={Styles.durationOptions}>
        {BUSY_DURATIONS.map(duration => (
          <div
            key={duration.value}
            className={clsx(Styles.durationOption, {
              [Styles.durationOptionSelected]: selectedDuration === duration.value
            })}
            onClick={() => handleDurationSelect(duration.value)}>
            <div className={Styles.durationContent}>
              <Text variant="nextBody" weight="semiBold" className={Styles.durationLabel}>
                {duration.value}min
              </Text>
            </div>
            <div className={Styles.durationRadio}>
              {selectedDuration === duration.value && (
                <div className={Styles.durationRadioSelected} />
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  )

  const renderBusyConfirmationContent = () => (
    <div className={Styles.summaryContent}>
      <div className={Styles.summaryIcon}>
        <ClockTimer />
      </div>
      <div className={Styles.summaryText}>
        <Text variant="nextTitle-3" weight="bold" className={Styles.summaryTitle}>
          {t('busy-mode-confirmation.title')}
        </Text>
        <Text variant="nextBody" className={Styles.summaryDescription}>
          {t('busy-mode-confirmation.description')}
        </Text>
      </div>
    </div>
  )

  const renderFooter = () => {
    if (isBusyConfirmationOpen) {
      return (
        <>
          <Button
            onClick={handleDeactivateBusyMode}
            disabled={isLoading || isUpdatingBusyStatus}
            loading={isUpdatingBusyStatus}
            shape="rounded"
            variant="tertiary"
            className={Styles.changeDurationButton}>
            {t('busy-mode-confirmation.buttons.deactivate')}
          </Button>
          <Button
            onClick={handleContinueBusyMode}
            disabled={isLoading || isUpdatingBusyStatus}
            shape="rounded"
            variant="branded"
            className={Styles.continueButton}>
            {t('busy-mode-confirmation.buttons.continue')}
          </Button>
        </>
      )
    }

    if (isBusySummaryOpen) {
      return (
        <>
          <Button 
            onClick={handleEditBusyDuration}
            shape="rounded"
            variant="tertiary"
            className={Styles.changeDurationButton}>
            {t('store-status.buttons.change-duration')}
          </Button>
          <Button
            onClick={handleConfirmBusySummary}
            disabled={isUpdatingBusyStatus}
            loading={isUpdatingBusyStatus}
            shape="rounded"
            variant="branded">
            {t('store-status.buttons.confirm')}
          </Button>
        </>
      )
    }

    if (isBusyDurationModalOpen) {
      return (
        <Button
          onClick={handleConfirmBusyDuration}
          disabled={selectedDuration === null || isUpdatingBusyStatus}
          loading={isUpdatingBusyStatus}
          shape="rounded"
          variant="branded">
          {t('store-status.buttons.confirm-duration')}
        </Button>
      )
    }

    if (selectedStatus === BUSY_STATUS.BUSY && isBusyEnabled) {
      return (
        <Button
          onClick={handleDefineBusyDuration}
          disabled={isUpdatingBusyStatus}
          shape="rounded"
          variant="branded">
          {t('store-status.buttons.set-busy-duration')} <ChevronRight />
        </Button>
      )
    }

    return (
      <Button
        onClick={handleUpdateStatus}
        disabled={selectedStatus === currentStatus || isLoading}
        loading={isLoading}
        shape="rounded"
        variant="branded">
        {t('store-status.buttons.update-status')}
      </Button>
    )
  }

  const renderHeader = () => {
    if (isBusyDurationModalOpen || isBusySummaryOpen) return null
    
    return (
      <div className={Styles.header}>
        {!isBusySummaryOpen && !isBusyConfirmationOpen && (
          <Text variant="nextTitle-3" weight="bold" className={Styles.title}>
            {modalTitle}
          </Text>
        )}
        <button onClick={onClose} className={Styles.closeButton}>
          <Cross />
        </button>
      </div>
    )
  }

  const renderContent = () => {
    if (error) {
      return (
        <div className={Styles.errorMessage}>
          <Text variant="nextCaption" className={Styles.errorText}>
            {error}
          </Text>
        </div>
      )
    }

    if (isBusyConfirmationOpen) {
      return renderBusyConfirmationContent()
    }

    if (isBusySummaryOpen) {
      return renderSummaryContent()
    }

    if (isBusyDurationModalOpen) {
      return renderDurationOptions()
    }

    return statusOptions.map(renderStatusOption)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={Styles.modal}>
        {renderHeader()}
        
        <div className={clsx(Styles.content, {
          [Styles.durationContent]: isBusyDurationModalOpen
        })}>
          {renderContent()}
        </div>

        <div className={Styles.footer}>
          {renderFooter()}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default UpdateStoreStatus
